import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import apiService from "../../services/api";

const s = {
  section: { marginTop: "1.5rem" },
  heading: { fontSize: "1rem", fontWeight: "600", color: "#001272", marginBottom: "0.75rem" },
  noteItem: {
    padding: "0.75rem",
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: "0.375rem",
    marginBottom: "0.5rem",
  },
  noteText: { fontSize: "0.875rem", color: "#374151", marginBottom: "0.25rem", whiteSpace: "pre-wrap" },
  noteMeta: { fontSize: "0.75rem", color: "#9ca3af" },
  deleteBtn: {
    float: "right", background: "none", border: "none", cursor: "pointer",
    color: "#dc2626", fontSize: "0.75rem", padding: "0",
  },
  addSection: { marginTop: "0.75rem" },
  textarea: {
    width: "100%", padding: "0.5rem 0.75rem", border: "1px solid #d1d5db",
    borderRadius: "0.375rem", fontSize: "0.875rem", outline: "none",
    boxSizing: "border-box", resize: "vertical", minHeight: "72px",
  },
  submitBtn: {
    marginTop: "0.4rem", padding: "0.35rem 0.9rem", fontSize: "0.8rem",
    fontWeight: "500", background: "#001272", color: "white",
    border: "none", borderRadius: "0.25rem", cursor: "pointer",
  },
  error: { fontSize: "0.8rem", color: "#dc2626", marginTop: "0.25rem" },
  empty: { fontSize: "0.85rem", color: "#9ca3af", fontStyle: "italic" },
};

const formatDate = (isoStr) => {
  if (!isoStr) return "";
  try {
    return new Date(isoStr).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return isoStr;
  }
};

const TutorNotesSection = ({ yearMonth, canonicalTutorId }) => {
  const queryClient = useQueryClient();
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const { data: notes = [] } = useQuery({
    queryKey: ["tutorNotes", yearMonth, canonicalTutorId],
    queryFn: () => apiService.getTutorNotes(yearMonth, canonicalTutorId),
    staleTime: 30 * 1000,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["tutorNotes", yearMonth, canonicalTutorId] });

  const handleAdd = async () => {
    if (!newNote.trim()) { setError("Note text is required."); return; }
    setSaving(true); setError(null);
    try {
      await apiService.addTutorNote(yearMonth, canonicalTutorId, newNote.trim());
      setNewNote("");
      invalidate();
    } catch (e) {
      setError(e.message || "Failed to add note.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    setSaving(true);
    try {
      await apiService.deleteTutorNote(yearMonth, canonicalTutorId, noteId);
      invalidate();
    } catch (e) {
      setError(e.message || "Failed to delete note.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={s.section}>
      <h3 style={s.heading}>Admin Notes</h3>
      {notes.length === 0 ? (
        <p style={s.empty}>No admin notes yet.</p>
      ) : (
        notes.map((note) => (
          <div key={note.id} style={s.noteItem}>
            <button style={s.deleteBtn} onClick={() => handleDelete(note.id)} disabled={saving}>
              Delete
            </button>
            <p style={s.noteText}>{note.text}</p>
            <span style={s.noteMeta}>
              {note.createdBy} · {formatDate(note.createdAt)}
            </span>
          </div>
        ))
      )}
      <div style={s.addSection}>
        <textarea
          style={s.textarea}
          placeholder="Add an admin note (not included in PDF payslip)..."
          value={newNote}
          onChange={(e) => { setNewNote(e.target.value); setError(null); }}
        />
        {error && <p style={s.error}>{error}</p>}
        <button style={{ ...s.submitBtn, opacity: saving ? 0.7 : 1 }} onClick={handleAdd} disabled={saving}>
          {saving ? "Saving..." : "Add Note"}
        </button>
      </div>
    </div>
  );
};

export default TutorNotesSection;
